import { useState, useEffect } from 'react';
import api from '../../services/api';
import { Users, PieChart, TrendingDown, AlertTriangle, BarChart3, RefreshCw, Calendar } from 'lucide-react';

const TIER_COLORS = { Bronze: '#CD7F32', Silver: '#C0C0C0', Gold: '#FFD700', Pearl: '#E8DCC8', Platinum: '#E5E4E2', Diamond: '#b9f2ff', 'Royal Diamond': '#ff4ecd' };
const DEP_COLORS = { no_deposits: '#6b7280', light: '#3B82F6', medium: '#22c55e', heavy: '#D4AF37', whale: '#ef4444' };
const ACT_COLORS = { inactive: '#ef4444', low: '#f97316', medium: '#eab308', high: '#22c55e', power_user: '#3B82F6' };

export default function SegmentationPage() {
  const [overview, setOverview] = useState(null);
  const [retention, setRetention] = useState(null);
  const [atRisk, setAtRisk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => { fetchAll(); }, [days]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [oRes, rRes, aRes] = await Promise.all([
        api.get(`/api/admin/analytics/segments/overview?days=${days}`),
        api.get(`/api/admin/analytics/segments/cohort-retention?months=6`),
        api.get(`/api/admin/analytics/segments/at-risk?days_inactive=7`),
      ]);
      setOverview(oRes.data);
      setRetention(rRes.data);
      setAtRisk(aRes.data);
    } catch {} finally { setLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  const total = overview?.total_players || 0;

  return (
    <div className="space-y-6" data-testid="segmentation-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><PieChart className="w-5 h-5 text-[#D4AF37]" /> Player Segmentation</h2>
          <p className="text-sm text-gray-400 mt-0.5">Cohort analysis, segmentation, and at-risk detection</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={e => setDays(Number(e.target.value))} className="bg-[#131822] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
          <button onClick={fetchAll} className="p-2 rounded-lg hover:bg-white/5 text-gray-400"><RefreshCw className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Top stat */}
      <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-[#D4AF37]/10">
          <Users className="w-7 h-7 text-[#D4AF37]" />
        </div>
        <div>
          <p className="text-3xl font-bold text-white">{total}</p>
          <p className="text-sm text-gray-400">Total Players Analyzed</p>
        </div>
        {atRisk && atRisk.count > 0 && (
          <div className="ml-auto flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/20">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <div>
              <p className="text-lg font-bold text-red-400">{atRisk.count}</p>
              <p className="text-[10px] text-red-400/70">At Risk ({atRisk.days_inactive}d inactive)</p>
            </div>
          </div>
        )}
      </div>

      {/* Segment Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* By Tier */}
        <SegmentCard title="By VIP Tier" icon={PieChart} data={overview?.by_tier || []} labelKey="tier" colorMap={TIER_COLORS} total={total} />

        {/* By Deposit Level */}
        <SegmentCard title="By Deposit Level" icon={BarChart3} data={overview?.by_deposit_level || []} labelKey="level" colorMap={DEP_COLORS} total={total} />

        {/* By Activity */}
        <SegmentCard title="By Activity Level" icon={TrendingDown} data={overview?.by_activity || []} labelKey="level" colorMap={ACT_COLORS} total={total} />
      </div>

      {/* Registration Cohorts */}
      {overview?.by_registration?.length > 0 && (
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><Calendar className="w-4 h-4 text-[#D4AF37]" /> Registration by Month</h3>
          <div className="flex items-end gap-1 h-28">
            {(() => {
              const maxCount = Math.max(...overview.by_registration.map(r => r.count), 1);
              return overview.by_registration.map((r, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                  <span className="text-[9px] text-gray-400">{r.count}</span>
                  <div className="w-full rounded-t" style={{ height: `${Math.max((r.count / maxCount) * 80, 4)}%`, background: 'linear-gradient(to top, #D4AF37, #f59e0b)' }} />
                  <span className="text-[7px] text-gray-600">{r.month?.slice(5)}</span>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* Cohort Retention */}
      {retention?.cohorts?.length > 0 && (
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#3B82F6]" /> Cohort Retention</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-500 border-b border-white/5">
                  <th className="text-left py-2 px-3 font-medium">Cohort</th>
                  <th className="text-right py-2 px-3 font-medium">Registered</th>
                  <th className="text-center py-2 px-3 font-medium">Month 0</th>
                  <th className="text-center py-2 px-3 font-medium">Month 1</th>
                  <th className="text-center py-2 px-3 font-medium">Month 2</th>
                  <th className="text-center py-2 px-3 font-medium">Month 3</th>
                </tr>
              </thead>
              <tbody>
                {retention.cohorts.map(c => (
                  <tr key={c.month} className="border-b border-white/[0.03]">
                    <td className="py-2 px-3 text-white font-medium">{c.month}</td>
                    <td className="py-2 px-3 text-right text-gray-400">{c.registered}</td>
                    {[0, 1, 2, 3].map(offset => {
                      const r = c.retention?.find(x => x.month_offset === offset);
                      const pct = r?.pct || 0;
                      const bg = pct > 60 ? 'bg-green-500/20 text-green-400' : pct > 30 ? 'bg-yellow-500/20 text-yellow-400' : pct > 0 ? 'bg-red-500/20 text-red-400' : 'text-gray-600';
                      return (
                        <td key={offset} className="py-2 px-3 text-center">
                          {r ? <span className={`px-2 py-0.5 rounded ${bg} text-[10px] font-medium`}>{pct}%</span> : <span className="text-gray-700">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* At-Risk Players */}
      {atRisk?.at_risk?.length > 0 && (
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" /> At-Risk Players ({atRisk.count} total, {atRisk.days_inactive}+ days inactive)
          </h3>
          <div className="space-y-1.5">
            {atRisk.at_risk.map(p => (
              <div key={p.id} className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-3 py-2">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm text-white">{p.username || p.email}</p>
                    <p className="text-[10px] text-gray-500 capitalize">Tier: {p.achievement_tier || 'Bronze'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-[#D4AF37]">${Number(p.cash_balance || 0).toLocaleString(undefined,{maximumFractionDigits:2})}</p>
                  <p className="text-[10px] text-gray-500">{(p.total_xp_earned || 0).toLocaleString()} XP</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SegmentCard({ title, icon: Icon, data, labelKey, colorMap, total }) {
  return (
    <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
      <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2"><Icon className="w-4 h-4 text-[#D4AF37]" /> {title}</h3>
      {data.length === 0 ? (
        <p className="text-xs text-gray-600 py-4 text-center">No data</p>
      ) : (
        <div className="space-y-2">
          {data.map(item => {
            const label = item[labelKey] || 'Unknown';
            const pct = total > 0 ? (item.count / total * 100) : 0;
            const color = colorMap[label] || '#D4AF37';
            return (
              <div key={label}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-xs text-gray-400 capitalize">{label.replace('_', ' ')}</span>
                  <span className="text-xs text-gray-300">{item.count} <span className="text-gray-600">({pct.toFixed(0)}%)</span></span>
                </div>
                <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.max(pct, 1)}%`, background: color }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
